import { useContext, useEffect, useState } from "react";
import { Redirect, Route, RouteComponentProps, RouteProps, Switch } from "react-router-dom";
import { ViewportContext } from "@monorepo/components";
import { routeConfig } from "@monorepo/constants/routeConfig";
import { lazyWithRetry } from "@monorepo/helpers";
import { DashboardPaymentActiveViewEnum, DashboardServiceActiveViewEnum } from "@monorepo/types";
import { ConnectedRouter } from "connected-react-router";
import { RegisterContextProvider } from "context/RegisterContext";
import { Help } from "pages/Help";
import { Login } from "pages/login/Login";
import { Notifications } from "pages/Notifications";
import { Profile } from "pages/Profile";
import { ReferralRoute } from "pages/ReferralRoute";
import Referred from "pages/Referred";
import { Register } from "pages/register/Register";
import { Services } from "pages/Services";

import { Dashboard } from "components/Dashboard/Dashboard";
import { useAppSelector } from "hooks";
import { browserHistory } from "store";

import { History } from "./pages/History";
import { Payments } from "./pages/Payments";

const TemplateForm = lazyWithRetry(() => import("@monorepo/components/src/Payments/TemplateForm/TemplateForm"));
const EditTemplateForm = lazyWithRetry(() => import("@monorepo/components/src/Payments/TemplateForm/EditTemplateForm"));
const ProfileMenuList = lazyWithRetry(() => import("components/Profile/ProfileContextMenu/ProfileContextMenu"));
const CampaignJoinSuccess = lazyWithRetry(() => import("components/Profile/Campaign/CampaignJoinSuccess"));
const FAQList = lazyWithRetry(() => import("components/FAQ/FAQ"));
const CartsList = lazyWithRetry(() => import("@monorepo/components/src/Cart/CartsList"));
const RecurringPaymentsList = lazyWithRetry(
  () => import("components/ServiceContextMenu/RecurringPaymentsList/RecurringPaymentsList")
);
const TemplatesList = lazyWithRetry(() => import("components/ServiceContextMenu/TemplatesList/TemplatesList"));
const HistoryList = lazyWithRetry(() => import("@monorepo/components/src/History/HistoryList"));
const NotificationsList = lazyWithRetry(() => import("components/Notifications/NotificationsList"));
const ServicesList = lazyWithRetry(() => import("components/ServiceContextMenu/ServicesList/ServicesList"));
const TemplatesInfo = lazyWithRetry(() => import("pages/TemplatesInfo"));
const RecurringPaymentInfo = lazyWithRetry(() => import("pages/RecurringPaymentInfo"));
const RecurringPaymentForm = lazyWithRetry(
  () => import("@monorepo/components/src/Payments/RecurringPaymentForm/RecurringPaymentForm")
);
const ReferralMenuList = lazyWithRetry(() => import("components/Referral/ReferralContextMenu/ReferralContextMenu"));

type TPrivateRouteType = RouteProps & {
  render: RouteProps["render"];
};

type TRoute = Omit<RouteProps, "component"> & {
  component?: JSX.Element;
  authorized?: boolean;
};

const routes: Array<TRoute> = [
  {
    path: [
      routeConfig.dashboard.payments.history.cartGroup.filter,
      routeConfig.dashboard.payments.history.cartGroup.search,
      routeConfig.dashboard.payments.history.cartGroup.order.service,
      routeConfig.dashboard.payments.history.cartGroup.order.self,
      routeConfig.dashboard.payments.history.cartGroup.summary.self,
      routeConfig.dashboard.payments.history.cartGroup.period.self,
      routeConfig.dashboard.payments.history.cartGroup.self,
      routeConfig.dashboard.payments.history.self,
    ],
    authorized: true,
    render: (
      props: RouteComponentProps<{
        orderId?: string;
        serviceId?: string;
        cartGroupId?: string;
        period?: string;
      }>
    ) => {
      return (
        <Dashboard
          context={
            <HistoryList
              cartGroupId={props.match.params.cartGroupId}
              activeOrderId={props.match.params.orderId}
              activePeriod={props.match.params.period}
            />
          }
          hideContextMenu={
            props.location.pathname.replace(/[0-9]/g, "") !==
              routeConfig.dashboard.payments.history.cartGroup.self.replace(":cartGroupId", "") &&
            props.location.pathname !== routeConfig.dashboard.payments.history.self
          }
          activePaymentView={DashboardPaymentActiveViewEnum.HISTORY}
        >
          <History {...props.match.params} />
        </Dashboard>
      );
    },
  },
  {
    path: [routeConfig.dashboard.notifications.notification.notificationId, routeConfig.dashboard.notifications.self],
    authorized: true,
    render: (props: RouteComponentProps<{ notificationId?: string }>) => {
      const { match } = props;

      return (
        <Dashboard
          context={<NotificationsList />}
          hideContextMenu={props.location.pathname !== routeConfig.dashboard.notifications.self}
        >
          <Notifications notificationId={match.params.notificationId} />
        </Dashboard>
      );
    },
  },
  {
    path: [
      routeConfig.dashboard.profile.personal,
      routeConfig.dashboard.profile.agreements,
      routeConfig.dashboard.profile.settings,
      routeConfig.dashboard.profile.self,
    ],
    authorized: true,
    render: (props: RouteComponentProps) => (
      <Dashboard
        hideContextMenu={props.location.pathname !== routeConfig.dashboard.profile.self}
        context={<ProfileMenuList />}
      >
        <Profile />
      </Dashboard>
    ),
  },
  {
    path: [
      routeConfig.dashboard.referral.coupons,
      routeConfig.dashboard.referral.referral,
      routeConfig.dashboard.referral.self,
    ],
    authorized: true,
    render: (props: RouteComponentProps) => (
      <Dashboard
        hideContextMenu={props.location.pathname !== routeConfig.dashboard.referral.self}
        context={<ReferralMenuList />}
      >
        <ReferralRoute />
      </Dashboard>
    ),
  },
  {
    path: [routeConfig.dashboard.help.faqGroup.groupId, routeConfig.dashboard.help.self],
    authorized: true,
    render: (props: RouteComponentProps<{ groupId?: string }>) => {
      const { match } = props;
      const groupId = match.params?.groupId;

      return (
        <Dashboard hideContextMenu={props.location.pathname !== routeConfig.dashboard.help.self} context={<FAQList />}>
          <Help faqGroupId={groupId} />
        </Dashboard>
      );
    },
  },
  {
    path: [routeConfig.dashboard.services.templates.addTemplate.self],
    authorized: true,
    render: (props: RouteComponentProps<{ recurringPaymentId?: string }>) => {
      return (
        <Dashboard
          hideContextMenu={props.location.pathname === routeConfig.dashboard.services.templates.addTemplate.self}
          activeServiceView={DashboardServiceActiveViewEnum.TEMPLATES}
          context={<TemplatesList />}
        >
          <TemplateForm />
        </Dashboard>
      );
    },
  },
  {
    path: [routeConfig.dashboard.services.templates.editTemplate.templateId],
    authorized: true,
    render: (props: RouteComponentProps<{ templateId?: string }>) => {
      return (
        <Dashboard
          hideContextMenu={props.location.pathname !== routeConfig.dashboard.services.recurringPayments.self}
          activeServiceView={DashboardServiceActiveViewEnum.TEMPLATES}
          context={<TemplatesList activeId={String(props.match.params.templateId)} />}
        >
          <EditTemplateForm id={String(props.match.params.templateId)} />
        </Dashboard>
      );
    },
  },
  {
    path: [routeConfig.dashboard.services.templates.self],
    authorized: true,
    component: (
      <Dashboard activeServiceView={DashboardServiceActiveViewEnum.TEMPLATES} context={<TemplatesList />}>
        <TemplatesInfo />
      </Dashboard>
    ),
  },
  {
    path: [routeConfig.dashboard.services.recurringPayments.editRecurringPayment.recurringPaymentId],
    authorized: true,
    render: (props: RouteComponentProps<{ recurringPaymentId?: string }>) => {
      return (
        <Dashboard
          hideContextMenu={props.location.pathname !== routeConfig.dashboard.services.recurringPayments.self}
          activeServiceView={DashboardServiceActiveViewEnum.RECURRING_PAYMENTS}
          context={<RecurringPaymentsList activeId={props.match.params.recurringPaymentId} />}
        >
          <RecurringPaymentForm id={props.match.params.recurringPaymentId} />
        </Dashboard>
      );
    },
  },
  {
    path: [routeConfig.dashboard.services.recurringPayments.addRecurringPayment.self],
    authorized: true,
    render: (props: RouteComponentProps) => {
      return (
        <Dashboard
          hideContextMenu={props.location.pathname !== routeConfig.dashboard.services.recurringPayments.self}
          activeServiceView={DashboardServiceActiveViewEnum.RECURRING_PAYMENTS}
          context={<RecurringPaymentsList />}
        >
          <RecurringPaymentForm />
        </Dashboard>
      );
    },
  },
  {
    path: [routeConfig.dashboard.services.recurringPayments.self],
    authorized: true,
    render: (props: RouteComponentProps) => {
      return (
        <Dashboard
          hideContextMenu={props.location.pathname !== routeConfig.dashboard.services.recurringPayments.self}
          activeServiceView={DashboardServiceActiveViewEnum.RECURRING_PAYMENTS}
          context={<RecurringPaymentsList />}
        >
          <RecurringPaymentInfo />
        </Dashboard>
      );
    },
  },
  {
    path: [
      routeConfig.dashboard.services.carInsurance,
      routeConfig.dashboard.services.fishermanTicket,
      routeConfig.dashboard.services.charityServices.charityId,
      routeConfig.dashboard.services.charityServices.self,
      routeConfig.dashboard.services.electricity,
      routeConfig.dashboard.services.category.categoryId.service.serviceId,
      routeConfig.dashboard.services.category.categoryId.self,
      routeConfig.dashboard.services.gsmTopUpServices.gsmTopUpServiceId,
      routeConfig.dashboard.services.gsmTopUpServices.self,
      routeConfig.dashboard.services.gsmVouchers.buyGsmVoucher.vendor.serviceId,
      routeConfig.dashboard.services.gsmVouchers.buyGsmVoucher.vendor.self,
      routeConfig.dashboard.services.gsmVouchers.buyGsmVoucher.self,
      routeConfig.dashboard.services.gsmVouchers.myVouchers.voucherId,
      routeConfig.dashboard.services.gsmVouchers.myVouchers.self,
      routeConfig.dashboard.services.gsmVouchers.howToUseGsmVoucher,
      routeConfig.dashboard.services.prepaidElectricity,
      routeConfig.dashboard.services.gsmVouchers.self,
      routeConfig.dashboard.services.self,
    ],
    authorized: true,
    render: (props: RouteComponentProps<{ gsmTopUpServiceId?: string; charityId?: string; voucherId?: string }>) => {
      return (
        <Dashboard
          activePaymentView={DashboardPaymentActiveViewEnum.SERVICES}
          hideContextMenu={
            props.location.pathname !== routeConfig.dashboard.services.self &&
            props.location.pathname !== routeConfig.dashboard.services.gsmVouchers.self
          }
          context={<ServicesList />}
        >
          <Services
            {...props.match.params}
            ignoreGroup={(props?.location?.state as { ignoreGroup: boolean })?.ignoreGroup ?? false}
          />
        </Dashboard>
      );
    },
  },
  {
    path: [
      routeConfig.login.password,
      routeConfig.loginAs,
      routeConfig.home,
      routeConfig.login.smsVerification,
      routeConfig.login.createPassword,
      routeConfig.login.self,
      routeConfig.login.resetPasswordEnterPhone,
      routeConfig.login.resetPasswordSubmit,
    ],
    render: (props: RouteComponentProps) => <Login path={props.location.pathname} />,
  },
  {
    path: [
      routeConfig.register.registerConfirm,
      routeConfig.register.createPassword,
      routeConfig.register.smsVerification,
      routeConfig.register.phoneVerification,
      routeConfig.register.self,
    ],
    component: (
      <RegisterContextProvider>
        <Register />
      </RegisterContextProvider>
    ),
  },
  {
    path: [routeConfig.referral],
    component: <Referred />,
  },
  {
    path: [routeConfig.dashboard.campaign.joinSuccess],
    authorized: true,
    render: (
      props: RouteComponentProps<{
        cartGroupId?: string;
        cartId?: string;
      }>
    ) => {
      return (
        <Dashboard
          context={
            <CartsList activeCartGroupId={props.match.params.cartGroupId} activeCartId={props.match.params.cartId} />
          }
          hideContextMenu={props.location.pathname !== routeConfig.dashboard.payments.self}
          activePaymentView={DashboardPaymentActiveViewEnum.CART_LIST}
        >
          <CampaignJoinSuccess />
        </Dashboard>
      );
    },
  },
  {
    path: [
      routeConfig.dashboard.payments.orderProcessing.self,
      routeConfig.dashboard.payments.cartGroup.editCartGroup,
      routeConfig.dashboard.payments.cartGroup.createCartGroup,
      routeConfig.dashboard.payments.cartGroup.editSharedCartGroup.clientId,
      routeConfig.dashboard.payments.cartGroup.editSharedCartGroupSuccess,
      routeConfig.dashboard.payments.cartGroup.shareCartGroup,
      routeConfig.dashboard.payments.cartGroup.shareCartGroupSuccess,
      routeConfig.dashboard.payments.cartGroup.sharedClients,
      routeConfig.dashboard.payments.cartGroup.overview,
      routeConfig.dashboard.payments.cartGroup.self,
      routeConfig.dashboard.payments.addService.template.service.edit.self,
      routeConfig.dashboard.payments.addService.template.providers.edit.self,
      routeConfig.dashboard.payments.addService.template.service.self,
      routeConfig.dashboard.payments.addService.template.providers.self,
      routeConfig.dashboard.payments.addService.recurringPayments.service.self,
      routeConfig.dashboard.payments.addService.recurringPayments.providers.self,
      routeConfig.dashboard.payments.addService.template.self,
      routeConfig.dashboard.payments.addService.serviceId,
      routeConfig.dashboard.payments.addService.self,
      routeConfig.dashboard.payments.repeatPayment.serviceId,
      routeConfig.dashboard.payments.repeatPayment.self,
      routeConfig.dashboard.payments.cart.cartId,
      routeConfig.dashboard.payments.paymentOverview,
      routeConfig.dashboard.order.cash,
      routeConfig.dashboard.order.mip,
      routeConfig.dashboard.payments.self,
      routeConfig.dashboard.self,
    ],
    authorized: true,
    render: (
      props: RouteComponentProps<{
        cartGroupId?: string;
        clientId?: string;
        serviceId?: string;
        cartId?: string;
        orderId?: string;
        providers?: string;
        templateId?: string;
      }>
    ) => {
      return (
        <Dashboard
          context={
            <CartsList activeCartGroupId={props.match.params.cartGroupId} activeCartId={props.match.params.cartId} />
          }
          hideContextMenu={props.location.pathname !== routeConfig.dashboard.payments.self}
          activePaymentView={DashboardPaymentActiveViewEnum.CART_LIST}
        >
          <Payments
            {...props.match.params}
            ignoreGroup={(props?.location?.state as { ignoreGroup: boolean })?.ignoreGroup ?? false}
            isPaymentBankOnly={(props?.location?.state as { isPaymentBankOnly: boolean })?.isPaymentBankOnly ?? false}
          />
        </Dashboard>
      );
    },
  },
];

const PrivateRoute = ({ render, ...rest }: TPrivateRouteType) => {
  const token = useAppSelector(({ authSlice }) => authSlice.access_token);

  if (!token) {
    return <Redirect to="/login" />;
  }
  return <Route {...rest} render={render} />;
};

export const Routes = () => {
  const [currentLocation, setCurrentLocation] = useState<string>("");
  const { isMobile } = useContext(ViewportContext);
  browserHistory.listen((location) => {
    if (currentLocation !== location?.pathname) {
      setCurrentLocation(location.pathname);
    }
  });

  useEffect(() => {
    if (currentLocation && isMobile) {
      //scrolling to top on mobile since scroll position keeps persistent per page view
      window.scrollTo(0, 0);
      //scrollIntoView is implemented in cartsList, cartsGroup, cartGroupItem, servicelistautocomplete but since it is on desktop, this code shouldnt break anyything
      //scrollIntoView doesnt break notifications scrolling on mobile while going to notification and back
    }
  }, [currentLocation, isMobile]);

  return (
    <>
      <ConnectedRouter history={browserHistory}>
        <Switch>
          {routes.map((route) => {
            const { path, authorized = false, component, render } = route;
            if (authorized) {
              return <PrivateRoute key={JSON.stringify(path)} path={path} render={render || (() => component)} />;
            }
            return <Route key={JSON.stringify(path)} exact path={path} render={render || (() => component)} />;
          })}
          <Redirect to="/login" />
        </Switch>
      </ConnectedRouter>
    </>
  );
};
